import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Space, Typography, Row, Col, Statistic, Spin, Button, message } from 'antd';
import { DashboardOutlined, YoutubeOutlined, LinkOutlined, ClockCircleOutlined, LoginOutlined, LogoutOutlined, FileTextOutlined, ExclamationCircleOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;

export default function StaffDashboard({ user }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clock, setClock] = useState(new Date());
  const [todayAtt, setTodayAtt] = useState(null);
  const [taskCount, setTaskCount] = useState(0);
  const clockRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { fetchDashboard(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clockRef.current = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(clockRef.current);
  }, []);

  useEffect(() => {
    api.get('/staff/my-today').then(r => setTodayAtt(r.data)).catch(() => {});
    api.get('/staff/my-tasks').then(r => {
      const tasks = Array.isArray(r.data) ? r.data : [];
      setTaskCount(tasks.filter(t => t.status === 'open' || t.status === 'reopened').length);
    }).catch(() => {});
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/staff/my-dashboard');
      setDashboard(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (loading && !dashboard) return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;

  const columns = [
    {
      title: 'Channel', dataIndex: 'channel_name', key: 'name',
      render: (name, r) => (
        <Space>
          <YoutubeOutlined style={{ color: '#ff0000' }} />
          <div>
            <Text strong>{name}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>{r.company_name}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'URL', dataIndex: 'channel_url', key: 'url',
      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-'
    },
    {
      title: 'Subscribers', dataIndex: 'subscriber_count', key: 'subs',
      render: (v) => (v || 0).toLocaleString('en-IN')
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><DashboardOutlined /> My Dashboard</Title>
          <Text type="secondary">Welcome, {user?.full_name || 'Staff Member'}</Text>
        </Col>
      </Row>

      {/* Live Clock + Today's Attendance */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 12, background: 'linear-gradient(135deg, #667eea22, #764ba222)', textAlign: 'center' }}>
            <ClockCircleOutlined style={{ fontSize: 24, color: '#667eea' }} />
            <div style={{ fontSize: 32, fontWeight: 700, color: '#333', marginTop: 4 }}>
              {clock.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
            </div>
            <div style={{ color: '#888', fontSize: 13 }}>
              {clock.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={16}>
          <Card style={{ borderRadius: 12, background: todayAtt?.checked_in ? 'linear-gradient(135deg, #52c41a11, #52c41a22)' : 'linear-gradient(135deg, #ff4d4f11, #ff4d4f22)' }}>
            <Row gutter={16} align="middle">
              <Col xs={12}>
                <Statistic title="Today Check-in" value={todayAtt?.check_in_time ? todayAtt.check_in_time : 'Not yet'}
                  prefix={<LoginOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: todayAtt?.check_in_time ? '#52c41a' : '#999', fontSize: 20 }} />
              </Col>
              <Col xs={12}>
                <Statistic title="Today Check-out" value={todayAtt?.check_out_time ? todayAtt.check_out_time : 'Pending'}
                  prefix={<LogoutOutlined style={{ color: todayAtt?.check_out_time ? '#ff4d4f' : '#999' }} />}
                  valueStyle={{ color: todayAtt?.check_out_time ? '#ff4d4f' : '#999', fontSize: 20 }} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {dashboard && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12}>
              <Card style={{ borderRadius: 12, background: '#fff7e6', borderColor: '#ffd591' }}>
                <Statistic
                  title="Channels Assigned to Me"
                  value={dashboard.total_channels || 0}
                  prefix={<YoutubeOutlined style={{ color: '#ff0000' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card style={{ borderRadius: 12, background: '#e6f7ff', borderColor: '#91d5ff' }}>
                <Statistic
                  title="Your Role"
                  value={(dashboard.staff?.role || 'staff').replace(/_/g, ' ')}
                  valueStyle={{ fontSize: 18, textTransform: 'capitalize' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="My Assigned Channels" style={{ borderRadius: 12, marginBottom: 24 }}>
            <Table
              dataSource={dashboard.channels || []}
              columns={columns}
              rowKey="channel_id"
              pagination={false}
            />
          </Card>
        </>
      )}

      {/* Pending Tasks Alert */}
      {taskCount > 0 && (
        <Card style={{ borderRadius: 12, marginBottom: 16, background: '#fffbe6', borderColor: '#ffe58f', cursor: 'pointer' }}
          onClick={() => navigate('/staff-tasks')}>
          <Row align="middle" gutter={12}>
            <Col><ExclamationCircleOutlined style={{ fontSize: 28, color: '#faad14' }} /></Col>
            <Col flex="auto">
              <Text strong style={{ fontSize: 16 }}>You have {taskCount} pending task{taskCount > 1 ? 's' : ''}</Text>
              <div><Text type="secondary">Click to view and complete your tasks</Text></div>
            </Col>
            <Col><FileTextOutlined style={{ fontSize: 20, color: '#faad14' }} /></Col>
          </Row>
        </Card>
      )}

      {/* My Monthly Reports */}
      <Card style={{ borderRadius: 12, marginBottom: 16, background: '#f0f5ff', borderColor: '#adc6ff' }}
        title={<span><FilePdfOutlined style={{ color: '#667eea' }} /> My Monthly Reports</span>}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Click on any month to download your complete report (attendance + salary + performance)</Text>
        <Row gutter={[12, 12]}>
          {(() => {
            const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const cards = [];
            const currentMonth = new Date().getMonth() + 1;
            const currentYear = new Date().getFullYear();
            const downloadReport = async (m, y) => {
              try {
                message.loading({ content: `Loading ${MONTHS[m-1]} ${y} report...`, key: 'pdf' });
                const res = await api.get(`/staff/my-hr?month=${m}&year=${y}`);
                const d = res.data;
                const p = d.profile || {};
                const a = d.attendance_summary || {};
                const attRows = (d.attendance || []).map(r =>
                  `<tr><td>${dayjs(r.att_date).format('DD MMM YYYY (ddd)')}</td><td style="color:${r.status === 'present' ? '#52c41a' : r.status === 'absent' ? '#ff4d4f' : r.status === 'holiday' ? '#722ed1' : '#faad14'};font-weight:bold">${(r.status || '').replace('_', ' ').toUpperCase()}</td><td>${r.check_in_time || '—'}</td><td>${r.check_out_time || '—'}</td></tr>`
                ).join('');
                const html = `<!DOCTYPE html><html><head><title>Monthly Report - ${d.user.full_name} - ${MONTHS[m-1]} ${y}</title>
                <style>body{font-family:Arial,sans-serif;padding:30px;max-width:900px;margin:auto}
                .header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px;text-align:center}
                .header h1{margin:0;font-size:22px}.header p{margin:5px 0 0;opacity:0.9}
                h2{color:#333;border-bottom:2px solid #667eea;padding-bottom:8px;margin-top:25px}
                table{width:100%;border-collapse:collapse;margin:10px 0}
                td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}
                th{background:#f0f5ff;font-weight:bold}
                .summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:15px 0}
                .summary-box{background:#f9f9f9;border-radius:8px;padding:12px;text-align:center;border:1px solid #eee}
                .summary-box .label{font-size:11px;color:#888;text-transform:uppercase}.summary-box .value{font-size:22px;font-weight:bold;margin-top:4px}
                .highlight{color:#52c41a}.deduction{color:#ff4d4f}.purple{color:#722ed1}
                .salary-card{background:#f0f5ff;border:2px solid #667eea;border-radius:12px;padding:20px;margin:15px 0}
                .net-salary{font-size:28px;font-weight:bold;color:#52c41a;text-align:center;margin:10px 0}
                .footer{text-align:center;color:#999;font-size:11px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}
                @media print{body{padding:15px}.header{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>
                <div class="header"><h1>${d.user.full_name} — Monthly Report</h1><p>${MONTHS[m-1]} ${y} | Bainsla Music Private Limited</p></div>
                <h2>Attendance Summary</h2>
                <div class="summary-grid">
                  <div class="summary-box"><div class="label">Working Days</div><div class="value">${a.working_days||0}</div></div>
                  <div class="summary-box"><div class="label">Present</div><div class="value highlight">${a.present||0}</div></div>
                  <div class="summary-box"><div class="label">Absent</div><div class="value deduction">${a.absent||0}</div></div>
                  <div class="summary-box"><div class="label">Half Day</div><div class="value" style="color:#faad14">${a.half_day||0}</div></div>
                  <div class="summary-box"><div class="label">Leave</div><div class="value">${a.leave||0}</div></div>
                  <div class="summary-box"><div class="label">Holiday</div><div class="value purple">${a.holiday||0}</div></div>
                </div>
                <h2>Salary Breakdown</h2>
                <div class="salary-card">
                  <table style="border:none"><tr><th style="background:transparent;border:none">Monthly Salary</th><td style="border:none;font-size:16px">₹${Number(a.monthly_salary||0).toLocaleString('en-IN')}</td>
                  <th style="background:transparent;border:none">Per Day</th><td style="border:none;font-size:16px">₹${Number(a.per_day_salary||0).toLocaleString('en-IN')}</td></tr>
                  <tr><th style="background:transparent;border:none">Absent (${Number(a.absent||0)} day)</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.absent_deduction ?? a.total_deduction ?? 0).toLocaleString('en-IN')}</td>
                  ${Number(a.pre_join_days||0) > 0 ? `<th style="background:transparent;border:none">Before Joining (${a.pre_join_days} day)</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.pre_join_deduction||0).toLocaleString('en-IN')}</td>` : '<th style="background:transparent;border:none"></th><td style="border:none"></td>'}</tr>
                  <tr><th style="background:transparent;border:none">Total Deduction</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.total_deduction||0).toLocaleString('en-IN')}</td>
                  <th style="background:transparent;border:none">Net Salary</th><td style="border:none"></td></tr></table>
                  <div class="net-salary">₹${Number(a.net_salary||0).toLocaleString('en-IN')}</div>
                </div>
                <h2>Daily Attendance</h2>
                <table><tr><th>Date</th><th>Status</th><th>Check-In</th><th>Check-Out</th></tr>${attRows || '<tr><td colspan="4" style="text-align:center;color:#999">No records</td></tr>'}</table>
                <div class="footer">Generated on ${new Date().toLocaleString('en-IN')} — Bainsla Music Data Dashboard | data.intubemedia.com</div>
                </body></html>`;
                const win = window.open('', '_blank');
                win.document.write(html);
                win.document.close();
                setTimeout(() => { win.print(); }, 500);
                message.success({ content: 'PDF ready!', key: 'pdf' });
              } catch (err) { message.error({ content: 'Failed to load report', key: 'pdf' }); }
            };
            for (let i = 0; i < 6; i++) {
              let m = currentMonth - i;
              let y = currentYear;
              if (m <= 0) { m += 12; y -= 1; }
              cards.push(
                <Col xs={12} sm={8} md={4} key={`${y}-${m}`}>
                  <Card size="small" hoverable
                    style={{ borderRadius: 10, textAlign: 'center' }}
                    onClick={() => downloadReport(m, y)}>
                    <div style={{ fontWeight: 'bold', fontSize: 16, color: '#333' }}>{MONTHS[m - 1]}</div>
                    <div style={{ color: '#888', fontSize: 12 }}>{y}</div>
                    <FilePdfOutlined style={{ fontSize: 20, color: '#667eea', marginTop: 8 }} />
                    <div style={{ fontSize: 11, color: '#667eea', marginTop: 4 }}>Download PDF</div>
                  </Card>
                </Col>
              );
            }
            return cards;
          })()}
        </Row>
      </Card>

      {dashboard && dashboard.total_channels === 0 && (
        <Card style={{ textAlign: 'center', padding: 40, borderRadius: 12 }}>
          <YoutubeOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
          <br /><br />
          <Text type="secondary">No channels assigned to you yet. Contact your admin to get channels assigned.</Text>
        </Card>
      )}
    </div>
  );
}
